import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase"; 
import { doc, getDoc } from "firebase/firestore"; // For fetching User Role

// Routes
import Login from "./routes/Login";
import Register from "./routes/Register";
import Recipes from "./routes/Recipes";
import RecipeDetail from "./routes/RecipeDetail";
import CreateRecipe from "./routes/CreateRecipe";
import EditRecipe from "./routes/EditRecipe";
import Profile from "./routes/Profile";
import AdminDashboard from "./routes/AdminDashboard";
import Navbar from "./components/Navbar";
import Favorites from "./routes/Favorites";
import ShoppingList from "./routes/ShoppingList";
import FeedbackModal from './components/FeedbackModal';
import Roadmap from './routes/Roadmap';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';

// UI
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import theme from './theme';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // --- 1. FIREBASE AUTH LISTENER (Replaces local storage checks) ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setIsLoggedIn(true);
        
        // Fetch extra user details (like username & role) from Firestore
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || "user");
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      } else {
        // User is logged out
        setIsLoggedIn(false);
        setUserRole(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe(); // Cleanup listener on unmount
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr auto', minHeight: '100vh' }}>
        
        <Navbar />
        
        <Box component="main" sx={{ width: '100%', overflowX: 'hidden', pb: { xs: 8, md: 0 } }}>
          <ErrorBoundary>
            <Routes>
              {/* Pass setters to Login so it can update state immediately if needed */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              
              <Route path="/recipes" element={isLoggedIn ? <Recipes /> : <Navigate to="/login" />} />
              <Route path="/profile" element={isLoggedIn ? <Profile /> : <Navigate to="/login" />} />
              <Route path="/favorites" element={isLoggedIn ? <Favorites /> : <Navigate to="/login" />} />
              <Route path="/shopping-list" element={isLoggedIn ? <ShoppingList /> : <Navigate to="/login" />} />
              <Route path="/recipes/:id" element={isLoggedIn ? <RecipeDetail /> : <Navigate to="/login" />} />
              <Route path="/create-recipe" element={isLoggedIn ? <CreateRecipe /> : <Navigate to="/login" />} />
              <Route path="/edit-recipe/:id" element={isLoggedIn ? <EditRecipe /> : <Navigate to="/login" />} />
              <Route path="/roadmap" element={isLoggedIn ? <Roadmap /> : <Navigate to="/login" />} />
              
              <Route 
                path="/admin" 
                element={isLoggedIn && userRole === "admin" ? <AdminDashboard /> : <Navigate to="/login" />} 
              />
              <Route path="/" element={isLoggedIn ? <Navigate to="/recipes" /> : <Navigate to="/login" />} />
            </Routes>
          </ErrorBoundary>
        </Box>

        {isLoggedIn && <Footer userRole={userRole} />}
      </Box>

      {isLoggedIn && <FeedbackModal />}
    </ThemeProvider>
  );
}

export default App;